import * as React from 'react';
import { cn } from '@/utils/cn';
import { formatTimestamp } from '@/utils/formatters';
import { MessageSender, type Message } from '@/types';
import type { PublicChatTheme } from '../themes/types';
import { WaveformPlayer } from '@/components/audio/WaveformPlayer';
import { toAbsoluteMediaUrl } from '@/utils/url';
import { base64ChunksToBlob } from '@/utils/file';
import { PublicMessageActions } from './PublicMessageActions';

interface PublicChatMessageProps {
  message: Message;
  theme: PublicChatTheme;
  onPlayAudio?: ((messageId: string) => void) | undefined;
  onStopAudio?: (() => void) | undefined;
  isPlaying?: boolean | undefined;
  isAudioLoading?: boolean | undefined;
}

export const PublicChatMessage: React.FC<PublicChatMessageProps> = ({ message, theme }) => {
  const [isVisualizingAudio, setIsVisualizingAudio] = React.useState(false);

  const isOwn = message.sender_type === MessageSender.USER;
  const isSystem = message.sender_type === MessageSender.SYSTEM;
  const isStreaming = message.sender_type === MessageSender.AI && !message.isTextComplete;
  const showTyping = isStreaming && (!message.content || message.content.length === 0);

  // 1. Resolve Server URL
  const serverAudioUrl = React.useMemo(() => {
    if (!message.audio_url) return null;
    return toAbsoluteMediaUrl(message.audio_url);
  }, [message.audio_url]);

  // 2. Resolve Local Blob from Buffer (Fallback for streaming)
  const [bufferedAudioUrl, setBufferedAudioUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (serverAudioUrl || !message.audioBuffer || message.audioBuffer.length === 0) {
      setBufferedAudioUrl(null);
      return;
    }
    const blob = base64ChunksToBlob(message.audioBuffer);
    if (!blob) {
      console.error('PublicChatMessage: failed to build audio blob from buffer');
      setBufferedAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setBufferedAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [message.audioBuffer, serverAudioUrl]);

  // Priority: Server URL > Buffered Blob
  const playableUrl = serverAudioUrl || bufferedAudioUrl;
  const hasAudio = !!playableUrl;

  const handlePlay = () => {
    setIsVisualizingAudio(true);
  };

  return (
    <div
      className={cn(
        'animate-slide-in flex w-full flex-col',
        isOwn ? 'mb-2 items-end' : 'mb-0 items-start',
        isSystem && 'items-center'
      )}
    >
      {isVisualizingAudio && playableUrl ? (
        /* --- WAVEFORM BUBBLE --- */
        <div
          className={cn(
            'relative w-fit pb-[6px] pl-3 pr-4 pt-4',
            theme.bubbles.ai,
            'min-w-[300px] max-w-[420px]'
          )}
        >
          <div className='flex items-center gap-2'>
            <WaveformPlayer
              url={playableUrl}
              height={30}
              barWidth={3}
              barGap={2}
              barRadius={3}
              waveColor='#FFFFFF'
              progressColor='#6EDF21'
              buttonClassName='text-accent-300 hover:text-white transition-colors'
              className='relative w-full pb-2'
              textClassName='absolute -bottom-1 -right-2 font-sans text-10 font-medium text-white/70'
              onFinish={() => setIsVisualizingAudio(false)}
              autoplay={true}
            />
          </div>
        </div>
      ) : (
        /* --- TEXT BUBBLE --- */
        <div
          className={cn(
            'relative w-fit px-6 pb-1 pt-5',
            isOwn && theme.bubbles.user,
            isOwn && theme.bubbles.textUser,
            !isOwn && !isSystem && theme.bubbles.ai,
            !isOwn && !isSystem && theme.bubbles.textAi,
            isSystem && theme.bubbles.system
          )}
        >
          <p className='whitespace-pre-wrap break-words'>
            {message.content}
            {showTyping && (
              <span className='animate-pulse text-sm italic opacity-50'>Typing...</span>
            )}
          </p>

          <p
            className={cn(
              'mt-1 font-sans text-12 font-normal',
              isOwn ? theme.bubbles.timestampUser : theme.bubbles.timestampAi,
              isSystem ? 'text-center' : 'text-right'
            )}
          >
            {formatTimestamp(message.created_at)}
          </p>
        </div>
      )}

      {/* --- PUBLIC MESSAGE ACTIONS --- */}
      {!isOwn && !isSystem && !isVisualizingAudio && message.isTextComplete && (
        <PublicMessageActions
          content={message.content}
          hasAudio={hasAudio}
          isPlaying={isVisualizingAudio}
          onPlay={handlePlay}
        />
      )}
    </div>
  );
};
